import { resolve } from './model.js';
import { formatAction, formatSceneHead } from './text.js';
/** Read-only reverse lookup. Text mentions are leads, never proof of presence/state. */
export function references(doc, address, opts = {}) {
    const offset = opts.offset ?? 0, limit = opts.limit ?? 20, context = opts.context ?? 3;
    for (const [name, value, minimum] of [['offset', offset, 0], ['limit', limit, 1], ['context', context, 0]]) {
        if (!Number.isSafeInteger(value) || value < minimum)
            throw new Error(`${name} must be an integer >= ${minimum}`);
    }
    const node = resolve(doc, address);
    if (node.t !== 'asset' && node.t !== 'state')
        throw new Error('references requires an asset or asset/state address; use read for episodes/scenes/actions');
    const asset = node.asset;
    const state = node.t === 'state' ? node.state.id : null;
    const names = [...new Set([asset.id, asset.name, ...asset.aliases])].filter(Boolean);
    const hits = [];
    for (const ep of doc.episodes)
        for (const scene of ep.scenes) {
            const bindings = [...(scene.location ? [scene.location] : []), ...scene.actors, ...scene.props]
                .filter(ref => ref.id === asset.id && (state === null || ref.states.includes(state)));
            if (bindings.length)
                hits.push({ kind: 'scene_binding', scene, matched: bindings.flatMap(ref => ref.states) });
            for (const action of scene.actions) {
                if (action.speaker?.id === asset.id && (state === null || action.speaker.state === state)) {
                    hits.push({ kind: 'speaker', scene, action, matched: [asset.id] });
                }
                // Prose does not identify a look. State queries include only explicit bindings.
                if (state === null) {
                    const matched = names.filter(name => action.content.includes(name));
                    if (matched.length)
                        hits.push({ kind: 'text_mention', scene, action, matched });
                }
            }
        }
    const end = offset + limit;
    return { asset_id: asset.id, state_id: state, total: hits.length,
        next_offset: end < hits.length ? end : null,
        items: hits.slice(offset, end).map(({ kind, scene, action, matched }) => {
            const index = action ? scene.actions.indexOf(action) : -1;
            const nearby = action ? scene.actions.slice(Math.max(0, index - context), index + context + 1) : [];
            const gutter = Math.max(0, ...nearby.map(row => row.anchor.length));
            return { kind, address: action?.anchor ?? scene.anchor, scene_address: scene.anchor,
                episode: scene.ep.no, matched,
                text: [formatSceneHead(doc, scene), ...nearby.map(row => (row === action ? '> ' : '  ') + formatAction(doc, row, gutter))].join('\n') };
        }),
    };
}
export function formatReferences(result) {
    return [
        `${result.asset_id}${result.state_id === null ? '' : '/' + result.state_id}  ${result.total} references · showing ${result.items.length}`,
        'scene_binding=explicit scene presence; speaker=explicit speaker; text_mention=name/alias mention only (not proof of presence).',
        ...result.items.map(hit => `${hit.address}  ${hit.kind}  ep=${hit.episode}\n${hit.text}`),
        result.next_offset === null ? 'end' : `Continue: --offset ${result.next_offset}`,
    ].join('\n\n');
}
